/**
 * features/admin/PromotionWizard.tsx
 * ------------------------------------------------------------------
 * Step 5 — Student Promotion System.
 *
 * Three-step wizard:
 *   1. Configure Rules — map each class to its promotion target
 *   2. Review Students — see every student's proposed outcome,
 *      handle exceptions (repeat, transfer, withdraw, graduate)
 *   3. Commit — write enrollment records + update student.currentClass
 *
 * Only admin and headteacher can access (gated by canManageTerms).
 * Designed for end-of-year workflow.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
import {
  doc,
  getDoc,
  collection,
  writeBatch,
} from 'firebase/firestore';
// Batch D: undefined-safe write wrapper
// setDoc goes through the safe wrapper; writeBatch stays unchanged
// (the batch.set call below is protected by an inline stripUndefined).
import { setDoc } from '@/lib/firebase';
import { stripUndefined } from '@/lib/sanitize';
import {
  ArrowUpDown,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Save,
  Check,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Users,
  GraduationCap,
  RefreshCw,
  LogOut,
  Settings,
  Zap,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getFormLevel } from '@/lib/grading';
import { sortClasses } from '@/lib/classSort';
import { Spinner } from '@/components/ui/primitives';
import StudentAvatar from '@/components/ui/StudentAvatar';
import {
  GradientHero,
  SoftCard,
  StatChip,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import type {
  Student,
  EnrollmentRecord,
  EnrollmentStatus,
  PromotionRuleSet,
} from '@/types';

/* ==================================================================
   TYPES
   ================================================================== */

type WizardStep = 'rules' | 'review' | 'commit';

interface StudentProposal {
  student: Student;
  currentClassName: string;
  action: EnrollmentStatus;
  targetClassId: string;
  targetClassName: string;
  notes: string;
}

const ACTION_LABELS: Record<EnrollmentStatus, string> = {
  enrolled: 'Enrolled',
  promoted: 'Promote',
  repeated: 'Repeat',
  transferred: 'Transfer Out',
  withdrawn: 'Withdraw',
  graduated: 'Graduate',
};

const ACTION_COLORS: Record<EnrollmentStatus, string> = {
  enrolled: 'bg-gray-50 text-gray-700 border-gray-200',
  promoted: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
  repeated: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  transferred: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
  withdrawn: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
  graduated: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
};

/* ==================================================================
   COMPONENT
   ================================================================== */

export default function PromotionWizard({ year }: { year: string }) {
  const { students: allStudents, classes } = useData();
  // Exclude 'pending' (Pastel-only, not yet enrolled) — never promotable.
  const students = allStudents.filter((s) => s.status !== 'pending');
  const { userData } = useAuth();

  const [step, setStep] = useState<WizardStep>('rules');
  const [rules, setRules] = useState<Record<string, string>>({});
  const [graduatingIds, setGraduatingIds] = useState<Set<string>>(new Set());
  const [proposals, setProposals] = useState<StudentProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');

  const nextYear = String(Number(year) + 1);
  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? id;

  // Sort classes by form level then name
  const sortedClasses = useMemo(
    () =>
      sortClasses(classes),
    [classes],
  );

  // Load saved promotion rules
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, COLLECTIONS.promotionRules, 'default'));
        if (snap.exists()) {
          const data = snap.data() as PromotionRuleSet;
          setRules(data.rules ?? {});
          setGraduatingIds(new Set(data.graduatingClassIds ?? []));
        } else {
          // Auto-suggest rules based on form levels
          const autoRules: Record<string, string> = {};
          const autoGrad = new Set<string>();
          for (const cls of sortedClasses) {
            const form = getFormLevel(cls.name);
            if (!form) continue;
            // Find next form level class
            const nextForm = isNaN(Number(form)) ? null : String(Number(form) + 1);
            if (nextForm) {
              const target = sortedClasses.find((c) =>
                c.id !== cls.id && getFormLevel(c.name) === nextForm,
              );
              if (target) autoRules[cls.id] = target.id;
            }
            // Mark highest form as graduating
            if (form === '5' || form === 'A2') {
              autoGrad.add(cls.id);
            }
          }
          setRules(autoRules);
          setGraduatingIds(autoGrad);
        }
      } catch (e) {
        console.error('Failed to load rules:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [sortedClasses]);

  // Build proposals when moving to review step
  function buildProposals() {
    const props: StudentProposal[] = [];
    for (const s of students) {
      const cls = classes.find((c) => c.id === s.currentClass);
      if (!cls) continue;
      const isGrad = graduatingIds.has(cls.id);
      const targetId = rules[cls.id];
      const targetCls = targetId ? classes.find((c) => c.id === targetId) : null;

      props.push({
        student: s,
        currentClassName: cls.name,
        action: isGrad ? 'graduated' : targetId ? 'promoted' : 'enrolled',
        targetClassId: isGrad ? '' : (targetId ?? s.currentClass),
        targetClassName: isGrad ? '—' : (targetCls?.name ?? '(same)'),
        notes: '',
      });
    }
    props.sort((a, b) => a.currentClassName.localeCompare(b.currentClassName) || a.student.fullName.localeCompare(b.student.fullName));
    setProposals(props);
  }

  function updateProposal(studentId: string, patch: Partial<StudentProposal>) {
    setProposals((ps) =>
      ps.map((p) => (p.student.id === studentId ? { ...p, ...patch } : p)),
    );
  }

  /* ---- SAVE RULES ----------------------------------------------- */

  async function saveRules() {
    setSaving(true);
    try {
      await setDoc(doc(db, COLLECTIONS.promotionRules, 'default'), {
        id: 'default',
        rules,
        graduatingClassIds: Array.from(graduatingIds),
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid ?? 'system',
      });
      setMsg({ ok: true, text: 'Promotion rules saved.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /* ---- COMMIT PROMOTIONS ---------------------------------------- */

  async function commitPromotions() {
    if (committed) return;
    setSaving(true);
    setMsg(null);
    try {
      const now = new Date().toISOString();
      const actor = userData?.uid ?? 'system';
      const batch = writeBatch(db);
      let count = 0;

      for (const p of proposals) {
        // Skip students that stay enrolled (no rule, no action)
        if (p.action === 'enrolled') continue;

        // Create enrollment record for the current year
        const enrollRef = doc(collection(db, COLLECTIONS.enrollments));
        const enrollment: Omit<EnrollmentRecord, 'id'> = {
          studentId: p.student.id,
          studentName: p.student.fullName,
          academicYear: year,
          classId: p.student.currentClass,
          className: p.currentClassName,
          formLevel: getFormLevel(p.currentClassName) ?? '',
          status: p.action,
          promotedToClassId: p.action === 'promoted' ? p.targetClassId : undefined,
          promotedToClassName: p.action === 'promoted' ? p.targetClassName : undefined,
          notes: p.notes || undefined,
          createdAt: now,
          createdBy: actor,
        };
        // Batch D: strip undefineds at the batch site since the
        // writeBatch API isn't covered by the foundation wrappers.
        // The Omit<EnrollmentRecord, 'id'> typing intentionally allows
        // undefined for optional fields, but Firestore rejects them.
        batch.set(enrollRef, stripUndefined(enrollment) as typeof enrollment);

        // Update student.currentClass (promoted or repeated)
        if (p.action === 'promoted' && p.targetClassId) {
          batch.update(doc(db, COLLECTIONS.students, p.student.id), {
            currentClass: p.targetClassId,
            updatedAt: now,
            updatedBy: actor,
          });
        }
        // Repeated students stay in the same class — no update needed.
        // Graduated / transferred / withdrawn -> mark 'left' so they drop
        // out of active rosters (kept for records; currentClass preserved).
        if (p.action === 'graduated' || p.action === 'transferred' || p.action === 'withdrawn') {
          batch.update(doc(db, COLLECTIONS.students, p.student.id), {
            status: 'left',
            exitReason: p.action,
            exitDate: now,
            lastClassId: p.student.currentClass,
            updatedAt: now,
            updatedBy: actor,
          });
        }

        count++;
      }

      await batch.commit();
      auditFromContext(userData, 'students_promoted', 'admissions', {
        targetType: 'student',
        details: { fromYear: year, toYear: nextYear, studentsUpdated: count },
      });
      setCommitted(true);
      setMsg({ ok: true, text: `Promotion complete. ${count} student records updated for ${year} → ${nextYear}.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /* ---- STATS ---------------------------------------------------- */

  const reviewStats = useMemo(() => {
    const byAction: Record<string, number> = {};
    for (const p of proposals) {
      byAction[p.action] = (byAction[p.action] ?? 0) + 1;
    }
    return byAction;
  }, [proposals]);

  const filteredProposals = useMemo(() => {
    const q = search.toLowerCase();
    return proposals.filter((p) => {
      if (classFilter && p.student.currentClass !== classFilter) return false;
      if (q && !p.student.fullName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [proposals, search, classFilter]);

  /* ---- RENDER --------------------------------------------------- */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner label="Loading promotion rules…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GradientHero
        icon={ArrowUpDown}
        title="Student Promotion"
        subtitle={`${year} → ${nextYear} · End-of-year class promotion wizard`}
      />

      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {msg.text}
          </div>
        </SoftCard>
      )}

      {/* Step indicator */}
      <SoftCard pad="p-4">
        <div className="flex items-center gap-2">
          {(['rules', 'review', 'commit'] as WizardStep[]).map((s, i) => {
            const labels = ['1. Configure Rules', '2. Review Students', '3. Commit'];
            const active = step === s;
            const done = (s === 'rules' && (step === 'review' || step === 'commit'))
                      || (s === 'review' && step === 'commit');
            return (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <ChevronRight size={14} className="text-gray-300" />}
                <button
                  onClick={() => {
                    if (s === 'rules') setStep('rules');
                    if (s === 'review' && (step === 'review' || step === 'commit')) setStep('review');
                  }}
                  disabled={s === 'commit'}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                    active
                      ? 'bg-[#1b3a6b] text-white shadow-md'
                      : done
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'text-gray-400'
                  }`}
                >
                  {done && <Check size={14} className="inline mr-1" />}
                  {labels[i]}
                </button>
              </div>
            );
          })}
        </div>
      </SoftCard>

      {step === 'rules' && renderRulesStep()}
      {step === 'review' && renderReviewStep()}
      {step === 'commit' && renderCommitStep()}
    </div>
  );

  /* ================================================================
     STEP 1 — CONFIGURE RULES
     ================================================================ */

  function renderRulesStep() {
    return (
      <div className="space-y-6">
        <SoftCard>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <Settings size={20} className="text-[#2d5295]" />
            Promotion Rules
          </h3>
          <p className="text-sm text-gray-500 mb-5">
            Map each class to its promotion target. Students in unmapped classes will be skipped.
            Mark graduating classes (final year) separately.
          </p>

          <div className="space-y-3">
            {sortedClasses.map((cls) => {
              const isGrad = graduatingIds.has(cls.id);
              return (
                <div
                  key={cls.id}
                  className={`flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-xl border ${
                    isGrad
                      ? 'border-purple-200 bg-purple-50/50 dark:border-purple-700 dark:bg-purple-900/20'
                      : rules[cls.id]
                      ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-700 dark:bg-emerald-900/10'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <GraduationCap size={16} className="text-gray-400 shrink-0" />
                    <span className="font-medium text-sm text-gray-900 dark:text-white">{cls.name}</span>
                  </div>

                  <ArrowRight size={16} className="text-gray-300 shrink-0 hidden sm:block" />

                  {isGrad ? (
                    <span className="text-sm text-purple-600 dark:text-purple-400 font-medium flex items-center gap-1">
                      <GraduationCap size={14} /> Graduating class
                    </span>
                  ) : (
                    <select
                      className={glassFieldClass('flex-1 sm:max-w-[240px]')}
                      value={rules[cls.id] ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRules((r) => {
                          const next = { ...r };
                          if (val) next[cls.id] = val;
                          else delete next[cls.id];
                          return next;
                        });
                      }}
                    >
                      <option value="">— no promotion (skip) —</option>
                      {sortedClasses
                        .filter((c) => c.id !== cls.id)
                        .map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                  )}

                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={isGrad}
                      onChange={(e) => {
                        setGraduatingIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) {
                            next.add(cls.id);
                            // Remove from rules if graduating
                            setRules((r) => { const n = { ...r }; delete n[cls.id]; return n; });
                          } else {
                            next.delete(cls.id);
                          }
                          return next;
                        });
                      }}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    Final year
                  </label>
                </div>
              );
            })}
          </div>
        </SoftCard>

        <SoftCard>
          <div className="flex items-center justify-between gap-4">
            <AccentButton variant="soft" icon={Save} onClick={saveRules} disabled={saving}>
              {saving ? 'Saving…' : 'Save Rules'}
            </AccentButton>
            <AccentButton
              icon={ChevronRight}
              onClick={() => { buildProposals(); setStep('review'); }}
            >
              Review Students →
            </AccentButton>
          </div>
        </SoftCard>
      </div>
    );
  }

  /* ================================================================
     STEP 2 — REVIEW STUDENTS
     ================================================================ */

  function renderReviewStep() {
    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatChip tone="blue" value={proposals.length} label="Total Students" icon={Users} />
          <StatChip tone="emerald" value={reviewStats.promoted ?? 0} label="Promote" icon={ArrowRight} />
          <StatChip tone="gold" value={reviewStats.repeated ?? 0} label="Repeat" icon={RefreshCw} />
          <StatChip tone="purple" value={reviewStats.graduated ?? 0} label="Graduate" icon={GraduationCap} />
          <StatChip tone="red" value={(reviewStats.transferred ?? 0) + (reviewStats.withdrawn ?? 0)} label="Transfer / Withdraw" icon={LogOut} />
        </div>

        {/* Filters */}
        <SoftCard pad="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search student…"
                className={glassFieldClass('pl-4')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className={glassFieldClass('sm:w-48')}
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">All Classes</option>
              {sortedClasses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </SoftCard>

        {/* Student proposals */}
        <SoftCard pad="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">Student</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Current Class</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Target</th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.map((p) => {
                  return (
                    <tr key={p.student.id} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/20">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <StudentAvatar student={p.student} size="sm" />
                          <span className="font-medium text-gray-900 dark:text-white">{p.student.fullName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-gray-600 dark:text-gray-400">
                        {p.currentClassName}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className={`text-xs px-3 py-1.5 rounded-lg border font-medium ${ACTION_COLORS[p.action]}`}
                          value={p.action}
                          onChange={(e) => {
                            const action = e.target.value as EnrollmentStatus;
                            const patch: Partial<StudentProposal> = { action };
                            if (action === 'repeated') {
                              patch.targetClassId = p.student.currentClass;
                              patch.targetClassName = p.currentClassName;
                            } else if (action === 'promoted') {
                              const rule = rules[p.student.currentClass];
                              patch.targetClassId = rule ?? p.student.currentClass;
                              patch.targetClassName = rule ? className(rule) : '(same)';
                            } else if (action === 'graduated' || action === 'transferred' || action === 'withdrawn') {
                              patch.targetClassId = '';
                              patch.targetClassName = '—';
                            }
                            updateProposal(p.student.id, patch);
                          }}
                        >
                          <option value="promoted">Promote</option>
                          <option value="repeated">Repeat</option>
                          <option value="graduated">Graduate</option>
                          <option value="transferred">Transfer Out</option>
                          <option value="withdrawn">Withdraw</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-600 dark:text-gray-400">
                        {p.action === 'promoted' ? p.targetClassName : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SoftCard>

        <SoftCard>
          <div className="flex items-center justify-between gap-4">
            <AccentButton variant="soft" icon={ChevronLeft} onClick={() => setStep('rules')}>
              ← Back to Rules
            </AccentButton>
            <AccentButton icon={Zap} onClick={() => setStep('commit')}>
              Proceed to Commit →
            </AccentButton>
          </div>
        </SoftCard>
      </div>
    );
  }

  /* ================================================================
     STEP 3 — COMMIT
     ================================================================ */

  function renderCommitStep() {
    const actionable = proposals.filter((p) => p.action !== 'enrolled');

    return (
      <div className="space-y-6">
        <SoftCard>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <AlertTriangle size={20} className="text-amber-500" />
            Confirm Promotion
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            You are about to process <strong>{actionable.length}</strong> student promotions for {year} → {nextYear}.
            This will:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 ml-4 mb-4">
            <li>• Create enrollment history records for each student</li>
            <li>• Update <strong>{reviewStats.promoted ?? 0}</strong> students to their new class</li>
            {(reviewStats.graduated ?? 0) > 0 && (
              <li>• Mark <strong>{reviewStats.graduated}</strong> students as graduated</li>
            )}
            {(reviewStats.repeated ?? 0) > 0 && (
              <li>• Record <strong>{reviewStats.repeated}</strong> students as repeating</li>
            )}
          </ul>
          <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
            This action cannot be easily undone. Please verify the review step carefully.
          </p>
        </SoftCard>

        {/* Summary by class */}
        <SoftCard>
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Summary by class</h4>
          <div className="space-y-2">
            {sortedClasses.map((cls) => {
              const classProposals = proposals.filter((p) => p.student.currentClass === cls.id);
              if (classProposals.length === 0) return null;
              const byAction: Record<string, number> = {};
              classProposals.forEach((p) => { byAction[p.action] = (byAction[p.action] ?? 0) + 1; });
              return (
                <div key={cls.id} className="flex items-center gap-3 text-sm">
                  <span className="font-medium w-32 shrink-0">{cls.name}</span>
                  <span className="text-gray-400">({classProposals.length} students)</span>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(byAction).map(([action, count]) => (
                      <span key={action} className={`text-xs px-2 py-0.5 rounded-full border ${ACTION_COLORS[action as EnrollmentStatus]}`}>
                        {ACTION_LABELS[action as EnrollmentStatus]}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SoftCard>

        <SoftCard>
          <div className="flex items-center justify-between gap-4">
            <AccentButton variant="soft" icon={ChevronLeft} onClick={() => setStep('review')}>
              ← Back to Review
            </AccentButton>
            {committed ? (
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold">
                <CheckCircle2 size={20} /> Promotion complete!
              </div>
            ) : (
              <AccentButton
                icon={Zap}
                onClick={commitPromotions}
                disabled={saving || actionable.length === 0}
              >
                {saving ? 'Processing…' : `Commit ${actionable.length} Promotions`}
              </AccentButton>
            )}
          </div>
        </SoftCard>
      </div>
    );
  }
}
